/**
 * [TD-47.6c-D.1] WeeklyMatchupBar - Weeks 1-18 traffic-light per Spec §3.4
 * + §5.1 line 376.
 *
 * Renders a horizontal bar of 18 cells. Each cell colored by the value in
 * `weeklyVector[week]`:
 *   "green"  → favorable matchup
 *   "yellow" → neutral
 *   "red"    → unfavorable
 *
 * When `weeklyVector` is empty `{}` (B.3.2 not yet ingested), renders an
 * honest "Weekly data pending" placeholder rather than fabricating cells -
 * matches Analytics Narrative Discipline Guard (A.7).
 *
 * `byeWeek` (optional) - that week's cell renders with a "BYE" badge
 * regardless of vector value (player won't play; no matchup to color).
 */

const WEEK_COUNT = 18;

const COLOR_MAP = {
  green: "bg-emerald-500/80 text-emerald-50",
  yellow: "bg-amber-400/80 text-amber-950",
  red: "bg-rose-500/80 text-rose-50",
};

const COLOR_RING = {
  green: "ring-emerald-400/40",
  yellow: "ring-amber-300/40",
  red: "ring-rose-400/40",
};

export function WeeklyMatchupBar({ weeklyVector, byeWeek = null }) {
  const hasData = weeklyVector && Object.keys(weeklyVector).length > 0;

  if (!hasData) {
    return (
      <div
        data-testid="weekly-matchup-bar-empty"
        className="flex items-center justify-center rounded-md border border-dashed border-neutral-300 bg-neutral-50/40 px-3 py-2 text-xs italic text-neutral-500"
      >
        Weekly matchup data pending
      </div>
    );
  }

  return (
    <div data-testid="weekly-matchup-bar" className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        {Array.from({ length: WEEK_COUNT }, (_, i) => {
          const week = i + 1;
          const isBye = byeWeek === week;
          const colorKey = weeklyVector[String(week)];
          const className = isBye
            ? "bg-neutral-300 text-neutral-700"
            : colorKey && COLOR_MAP[colorKey]
              ? COLOR_MAP[colorKey]
              : "bg-neutral-200 text-neutral-500";
          const ringClass = !isBye && colorKey && COLOR_RING[colorKey]
            ? `ring-1 ${COLOR_RING[colorKey]}`
            : "";
          return (
            <div
              key={week}
              data-testid={`weekly-matchup-cell-${week}`}
              data-color={isBye ? "bye" : (colorKey || "unknown")}
              className={`flex h-7 flex-1 min-w-0 items-center justify-center rounded text-[10px] font-semibold ${className} ${ringClass}`}
              title={isBye ? `Week ${week} - BYE` : `Week ${week} - ${colorKey || "no data"}`}
            >
              {isBye ? "BYE" : week}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default WeeklyMatchupBar;
